import type { IdZonaOperacaoPorta } from '../../dominio/didatico/oficinaCorte'
import { contornoVidroPorta } from '../veiculo/formasCarroceria'
import type { FaixaLongitudinal, PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'

export interface ZonaOperacaoPorta3D {
  id: IdZonaOperacaoPorta
  nome: string
  posicao: [number, number, number]
  /** Inclinação recomendada da ferramenta no plano da porta. */
  anguloGraus: number
  /** Área semântica clicável no plano da peça: altura (Y) e largura (Z). */
  areaContato: readonly [altura: number, largura: number]
  /** Define se a ferramenta entra orientada paralela ao veículo ao longo do eixo longitudinal Z */
  orientacaoParalela?: boolean
  alturaEsquadria?: number
}

export interface AjusteFerramentaPorta {
  deslocamentoY: number
  deslocamentoZ: number
  anguloGraus: number
}

export interface AvaliacaoContatoPorta {
  zona: IdZonaOperacaoPorta | null
  distancia: number | null
  contatoValido: boolean
  orientacaoValida: boolean
}

export const AJUSTE_FERRAMENTA_INICIAL: AjusteFerramentaPorta = {
  deslocamentoY: 0,
  deslocamentoZ: 0,
  anguloGraus: 0,
}

export const TOLERANCIA_CONTATO_PORTA = 0.2
export const TOLERANCIA_ANGULO_PORTA_GRAUS = 30

export type IdPortaOficina =
  | 'portaDianteiraEsquerda'
  | 'portaDianteiraDireita'
  | 'portaTraseiraEsquerda'
  | 'portaTraseiraDireita'

export function dadosDaPorta(
  perfil: PerfilVeiculo,
  id: IdPortaOficina,
): { lado: 'esquerdo' | 'direito'; faixa: FaixaLongitudinal } {
  return {
    lado: id.endsWith('Esquerda') ? 'esquerdo' : 'direito',
    faixa: id.startsWith('portaDianteira') ? perfil.portaDianteira : perfil.portaTraseira,
  }
}

/** Pontos didáticos derivados das mesmas dimensões usadas pela PortaDidatica. */
export function zonasOperacaoDaPorta(
  perfil: PerfilVeiculo,
  idPorta: IdPortaOficina,
): readonly ZonaOperacaoPorta3D[] {
  const { lado, faixa } = dadosDaPorta(perfil, idPorta)
  const sinal = lado === 'esquerdo' ? 1 : -1
  const x = sinal * (perfil.largura / 2 + 0.055)
  const yBaixo = perfil.topoSoleiraY
  const yCintura = perfil.cinturaY
  const meioY = (yBaixo + yCintura) / 2
  const meioZ = (faixa.zDianteiro + faixa.zTraseiro) / 2

  const vidro = contornoVidroPorta(perfil, faixa, idPorta.startsWith('portaDianteira'))
  const alturaEsquadria = vidro.yTopo - vidro.yBase

  return [
    { id: 'macaneta', nome: 'Maçaneta', posicao: [x, yCintura - 0.09, meioZ - (faixa.zDianteiro - faixa.zTraseiro) * 0.28], anguloGraus: 0, areaContato: [0.12, 0.18] },
    { id: 'chapa', nome: 'Chapa externa', posicao: [x, meioY, meioZ], anguloGraus: 0, areaContato: [0.34, 0.52] },
    { id: 'trava', nome: 'Pino da trava', posicao: [x, meioY - 0.04, faixa.zTraseiro + 0.06], anguloGraus: 90, areaContato: [0.2, 0.2] },
    { id: 'dobradicaSuperior', nome: 'Dobradiça superior', posicao: [x, yCintura - 0.14, faixa.zDianteiro - 0.02], anguloGraus: 0, areaContato: [0.18, 0.18] },
    { id: 'dobradicaInferior', nome: 'Dobradiça inferior', posicao: [x, yBaixo + 0.14, faixa.zDianteiro - 0.02], anguloGraus: 0, areaContato: [0.18, 0.18] },
    { id: 'limitador', nome: 'Limitador', posicao: [x, meioY + 0.02, faixa.zDianteiro - 0.09], anguloGraus: 0, areaContato: [0.16, 0.18] },
    { id: 'fiacao', nome: 'Fiação', posicao: [x, meioY - 0.1, faixa.zDianteiro + 0.02], anguloGraus: 0, areaContato: [0.16, 0.18] },
    { id: 'esquadria', nome: 'Esquadria da janela', posicao: [x, (vidro.yBase + vidro.yTopo) / 2, (vidro.frenteTopo + vidro.trasTopo) / 2], anguloGraus: 0, areaContato: [0.2, 0.52], alturaEsquadria },
    { id: 'paralama', nome: 'Para-lama dianteiro', posicao: [x, 0.74, perfil.eixoDianteiroZ], anguloGraus: 0, areaContato: [0.36, 0.40], orientacaoParalela: true },
  ]
}

export function posicaoFerramentaNaPorta(
  perfil: PerfilVeiculo,
  idPorta: IdPortaOficina,
  idZona: IdZonaOperacaoPorta | null,
  ajuste: AjusteFerramentaPorta,
): [number, number, number] | null {
  const zona = zonaOperacaoPorId(perfil, idPorta, idZona)
  if (!zona) return null
  return [zona.posicao[0], zona.posicao[1] + ajuste.deslocamentoY, zona.posicao[2] + ajuste.deslocamentoZ]
}

/** Avalia contato e orientação sem depender do marcador originalmente escolhido. */
export function avaliarContatoFerramentaNaPorta(
  perfil: PerfilVeiculo,
  idPorta: IdPortaOficina,
  posicao: [number, number, number] | null,
  anguloGraus: number,
): AvaliacaoContatoPorta {
  if (!posicao) return { zona: null, distancia: null, contatoValido: false, orientacaoValida: false }

  const zonas = zonasOperacaoDaPorta(perfil, idPorta)
  const ordenadas = zonas
    .map((zona) => ({
      zona,
      distancia: Math.hypot(posicao[1] - zona.posicao[1], posicao[2] - zona.posicao[2]),
      alcanceNormalizado: Math.hypot(
        (posicao[1] - zona.posicao[1]) / (zona.areaContato[0] / 2),
        (posicao[2] - zona.posicao[2]) / (zona.areaContato[1] / 2),
      ),
    }))
    .sort((a, b) => a.alcanceNormalizado - b.alcanceNormalizado)
  const proxima = ordenadas[0]
  if (!proxima || (proxima.alcanceNormalizado > 1 && proxima.distancia > TOLERANCIA_CONTATO_PORTA)) {
    return { zona: null, distancia: proxima?.distancia ?? null, contatoValido: false, orientacaoValida: false }
  }

  const diferencaBruta = Math.abs(anguloGraus - proxima.zona.anguloGraus) % 180
  const diferenca = Math.min(diferencaBruta, 180 - diferencaBruta)
  return {
    zona: proxima.zona.id,
    distancia: proxima.distancia,
    contatoValido: true,
    orientacaoValida: diferenca <= TOLERANCIA_ANGULO_PORTA_GRAUS,
  }
}

export function zonaOperacaoPorId(
  perfil: PerfilVeiculo,
  idPorta: IdPortaOficina,
  idZona: IdZonaOperacaoPorta | null,
): ZonaOperacaoPorta3D | null {
  if (!idZona) return null
  return zonasOperacaoDaPorta(perfil, idPorta).find((zona) => zona.id === idZona) ?? null
}
